import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ForkKnife, X } from '@phosphor-icons/react';
import { medirTinta } from '../../utils/medirTinta';

const buildImageUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `https://${url}`;
};

// Lado del botón en px (w-16).
const LADO = 64;

// Parte del dibujo que es sombra desplazada (abajo a la derecha) y no círculo.
// Medido en el diseño subido: contorno de 931px dentro de 969px de tinta.
const SOMBRA = 0.04;

// Curva de salida suave (rápida al principio, se asienta al final). Con tween en
// vez de resorte la tarjeta no rebota ni se siente trabada.
const SUAVE = [0.22, 1, 0.36, 1];

/**
 * Dónde cae el círculo del diseño dentro del botón, en px. La imagen se pinta
 * con object-contain, así que primero se ubica el lienzo y luego la tinta.
 */
function caraDesde(tinta) {
  const anchoImg = tinta.aspecto >= 1 ? LADO : LADO * tinta.aspecto;
  const altoImg = tinta.aspecto >= 1 ? LADO / tinta.aspecto : LADO;
  const ox = (LADO - anchoImg) / 2;
  const oy = (LADO - altoImg) / 2;
  const d = Math.min(tinta.ancho * anchoImg, tinta.alto * altoImg) * (1 - SOMBRA);
  return { left: ox + tinta.x * anchoImg, top: oy + tinta.y * altoImg, d };
}

/**
 * Botón flotante "Menú" del home (abajo a la derecha) y su lista de
 * categorías.
 *
 * Al tocarlo se oscurece la pantalla y aparece, encima del botón, una tarjeta
 * con las categorías y cuántos productos tiene cada una. Tocar una lleva a esa
 * categoría.
 *
 * El diseño del botón es una imagen que sube el admin desde el dashboard
 * (Business.menuButtonImage); sin ella se dibuja uno por defecto. Al abrir, la
 * imagen NO se reemplaza: se le pone encima un disco con la X, medido sobre el
 * círculo del diseño, así conserva su contorno y su sombra y no cambia de
 * tamaño.
 *
 * Va en un portal al body: la sección del home vive dentro de un contenedor
 * con transform (el slider), y ahí `position: fixed` no se ancla a la pantalla.
 *
 * Props:
 *   - categorias: categorías del menú, cada una con su arreglo `products`
 *   - activa: id de la categoría que se está viendo, para marcarla
 *   - onSelect(categoriaId): lleva a la categoría
 *   - imagen: URL del diseño del botón (opcional)
 *   - subir: true cuando la barra del carrito está visible, para no taparla
 */
export default function MenuFlotante({ categorias = [], activa, onSelect, imagen, subir = false }) {
  const [abierto, setAbierto] = useState(false);
  const urlImagen = buildImageUrl(imagen);
  const [cara, setCara] = useState(null);

  useEffect(() => {
    let vivo = true;
    setCara(null);
    medirTinta(urlImagen).then((t) => { if (vivo && t) setCara(caraDesde(t)); });
    return () => { vivo = false; };
  }, [urlImagen]);

  if (categorias.length === 0) return null;

  const elegir = (id) => {
    setAbierto(false);
    onSelect?.(id);
  };

  // Disco de la X: sobre el círculo del diseño (1px adentro, para que se vea su
  // contorno) o, sin imagen, sobre el botón por defecto completo.
  const disco = urlImagen
    ? cara && { left: cara.left + 1, top: cara.top + 1, width: cara.d - 2, height: cara.d - 2 }
    : { left: 0, top: 0, width: LADO, height: LADO };

  return createPortal(
    <>
      <AnimatePresence>
        {abierto && (
          <motion.div
            key="velo"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={() => setAbierto(false)}
            className="fixed inset-0 z-[60] bg-black/45"
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {abierto && (
          <motion.div
            key="lista"
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: 0.24, ease: SUAVE } }}
            exit={{ opacity: 0, y: 6, scale: 0.98, transition: { duration: 0.14, ease: 'easeIn' } }}
            style={{ transformOrigin: 'bottom right', willChange: 'transform, opacity' }}
            className={`fixed right-3 z-[61] w-56 bg-white rounded-2xl shadow-2xl overflow-hidden ${
              subir ? 'bottom-[148px]' : 'bottom-[84px]'
            }`}
          >
            <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              Menú
            </p>
            <ul className="max-h-[50dvh] overflow-y-auto overscroll-contain pb-1.5">
              {categorias.map((cat) => {
                const esActiva = cat._id === activa;
                return (
                  <li key={cat._id}>
                    <button
                      type="button"
                      onClick={() => elegir(cat._id)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-2 text-left active:bg-gray-50"
                    >
                      <span className={`text-[14px] truncate ${esActiva ? 'font-bold text-primary-950' : 'font-medium text-gray-800'}`}>
                        {cat.name}
                      </span>
                      <span className={`text-[13px] flex-shrink-0 tabular-nums ${esActiva ? 'font-bold text-primary-950' : 'text-gray-400'}`}>
                        {cat.products?.length ?? 0}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-label={abierto ? 'Cerrar menú de categorías' : 'Ver categorías del menú'}
        aria-expanded={abierto}
        className={`fixed right-3 w-16 h-16 active:scale-95 transition-[bottom,transform] duration-300 ease-out ${
          abierto ? 'z-[61]' : 'z-30'
        } ${subir ? 'bottom-[80px]' : 'bottom-4'}`}
      >
        {urlImagen ? (
          <img src={urlImagen} alt="" className="w-full h-full object-contain" />
        ) : (
          <span className="w-full h-full rounded-full bg-white ring-2 ring-primary-950 flex flex-col items-center justify-center shadow-lg">
            <ForkKnife size={22} weight="fill" className="text-primary-950" />
            <span className="text-[10px] font-extrabold leading-none text-primary-950 mt-0.5">MENÚ</span>
          </span>
        )}

        {disco && (
          <motion.span
            aria-hidden="true"
            initial={false}
            animate={{ opacity: abierto ? 1 : 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="absolute rounded-full bg-primary-100 flex items-center justify-center pointer-events-none"
            style={disco}
          >
            <motion.span
              initial={false}
              animate={{ rotate: abierto ? 0 : -90, scale: abierto ? 1 : 0.6 }}
              transition={{ duration: 0.24, ease: SUAVE }}
              className="flex"
            >
              <X size={22} weight="bold" className="text-gray-900" />
            </motion.span>
          </motion.span>
        )}
      </button>
    </>,
    document.body
  );
}
